/** @jsxImportSource @emotion/react */
'use client';

import {
  Controller,
  type Control,
  type FieldPath,
  type FieldValues,
  type PathValue,
} from 'react-hook-form';
import { FormField as PresentationalFormField } from '../atoms/FormField';
import { Input, Textarea } from '../atoms/Input';

export interface ControlledFormFieldProps<TFieldValues extends FieldValues> {
  readonly name: FieldPath<TFieldValues>;
  readonly control: Control<TFieldValues>;
  readonly label: string;
  readonly type?: React.HTMLInputTypeAttribute;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly required?: boolean;
  readonly autoComplete?: string;
  readonly id?: string;
  /** Use Afrodita Textarea instead of Input. */
  readonly multiline?: boolean;
  readonly maxLength?: number;
  readonly readOnly?: boolean;
}

/**
 * RHF-controlled field: Afrodita label + input + error with a11y wiring.
 */
export function ControlledFormField<TFieldValues extends FieldValues>({
  name,
  control,
  label,
  type = 'text',
  placeholder,
  disabled,
  required,
  autoComplete,
  id,
  multiline = false,
  maxLength,
  readOnly,
}: ControlledFormFieldProps<TFieldValues>) {
  const fieldId = id ?? String(name);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const errorMessage = fieldState.error?.message;
        const errorId = errorMessage ? `${fieldId}-error` : undefined;

        return (
          <PresentationalFormField
            label={label}
            htmlFor={fieldId}
            error={errorMessage}
            // Keep label text stable for a11y/tests — required is on the control, not Afrodita's "*" mark.
          >
            {multiline ? (
              <Textarea
                id={fieldId}
                name={field.name}
                ref={field.ref}
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder={placeholder}
                disabled={disabled}
                maxLength={maxLength}
                readOnly={readOnly}
                required={required}
                aria-required={required || undefined}
                aria-invalid={!!errorMessage}
                aria-describedby={errorId}
              />
            ) : (
              <Input
                id={fieldId}
                name={field.name}
                ref={field.ref}
                type={type}
                value={
                  (field.value as
                    | PathValue<TFieldValues, FieldPath<TFieldValues>>
                    | undefined) ?? ''
                }
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder={placeholder}
                disabled={disabled}
                autoComplete={autoComplete}
                maxLength={maxLength}
                readOnly={readOnly}
                required={required}
                aria-required={required || undefined}
                aria-invalid={!!errorMessage}
                aria-describedby={errorId}
              />
            )}
          </PresentationalFormField>
        );
      }}
    />
  );
}
